import * as React from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface DatePickerProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  disabledDates?: Date[];
  minDate?: Date;
  maxDate?: Date;
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = "Selecciona una fecha",
  disabled = false,
  className,
  disabledDates = [],
  minDate,
  maxDate,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal h-10 sm:h-12 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl sm:rounded-2xl transition-all duration-200 text-sm",
            !date && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? (
            format(date, "dd/MM/yyyy", { locale: es })
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="w-auto p-0 bg-gradient-to-br from-card/90 to-card/70 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-2xl">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-lg font-semibold text-foreground">
            Seleccionar Fecha
          </DialogTitle>
        </DialogHeader>
        <div className="p-6 pt-2">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(selectedDate) => {
              onDateChange?.(selectedDate);
              setOpen(false);
            }}
            disabled={(date) => {
              // Obtener la fecha actual sin hora
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              
              // Deshabilitar fines de semana
              const day = date.getDay();
              const isWeekend = day === 0 || day === 6; // 0 = domingo, 6 = sábado
              
              // Deshabilitar fechas pasadas
              const isPast = date < today;
              
              // Deshabilitar fechas específicas
              const isDisabledDate = disabledDates.some(
                (disabledDate) => 
                  disabledDate.getFullYear() === date.getFullYear() &&
                  disabledDate.getMonth() === date.getMonth() &&
                  disabledDate.getDate() === date.getDate()
              );
              
              // Deshabilitar fechas fuera del rango
              const isBeforeMin = minDate ? date < minDate : false;
              const isAfterMax = maxDate ? date > maxDate : false;
              
              return isWeekend || isPast || isDisabledDate || isBeforeMin || isAfterMax;
            }}
            initialFocus
            className="rounded-xl"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
